import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { CheckCircle2, Globe, MessageCircle, BookOpen, Star, Calendar, Menu, X } from "lucide-react";
import TestLanguageDropdown from "../components/TestLanguageDropdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to the section matching the current route (shareable section links)
  useEffect(() => {
    const routeToSection = {
      "/programs": "programs",
      "/pricing": "pricing",
      "/language-coach": "coach",
      "/testimonials": "testimonials",
      "/book": "book",
    };
    const sectionId = routeToSection[location.pathname];
    if (sectionId) {
      const scroll = () => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      };
      // Delay so the section is rendered before scrolling
      const timer = setTimeout(scroll, 150);
      return () => clearTimeout(timer);
    }
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-colors duration-300 ${scrolled ? 'bg-[#04539F]/95 border-white/10 shadow-lg' : 'bg-white/10 border-white/20'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button 
              onClick={() => navigate("/")}
              className="text-2xl font-bold text-white hover:text-white/90 transition-colors cursor-pointer"
              style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.6)' }}
              data-testid="logo-home-link"
            >
              LeProfBerni
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8 items-center">
              <button onClick={() => navigate("/programs")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-programs">Programs</button>
              <button onClick={() => navigate("/language-coach")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-coach">Language Coach</button>
              <button onClick={() => navigate("/testimonials")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-testimonials">Testimonials</button>
              <button onClick={() => navigate("/about")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-about">About</button>
              <button onClick={() => navigate("/contact")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-contact">Contact</button>
              <button onClick={() => navigate("/store")} className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-store">Store</button>
              <TestLanguageDropdown />
              <a href="#book" className="text-white hover:text-white/80 transition-colors" style={{ textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)' }} data-testid="nav-book" onClick={(e) => { e.preventDefault(); navigate("/book"); }}>Book Session</a>
              <img 
                src="https://customer-assets.emergentagent.com/job_languagecoach/artifacts/iatwncw6_Logo%20le%20prof.png" 
                alt="Le Prof Berni Logo" 
                className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-lg ml-4"
                data-testid="nav-logo"
              />
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#04539F] border-t border-white/20" data-testid="mobile-menu">
            <div className="px-4 py-6 space-y-4">
              <button 
                onClick={() => { navigate("/programs"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-programs"
              >
                Programs
              </button>
              <button 
                onClick={() => { navigate("/language-coach"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-coach"
              >
                Language Coach
              </button>
              <button 
                onClick={() => { navigate("/testimonials"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-testimonials"
              >
                Testimonials
              </button>
              <button 
                onClick={() => { navigate("/about"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-about"
              >
                About
              </button>
              <button 
                onClick={() => { navigate("/contact"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-contact"
              >
                Contact
              </button>
              <button 
                onClick={() => { navigate("/store"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-store"
              >
                Store
              </button>
              <TestLanguageDropdown isMobile={true} />
              <button 
                onClick={() => { navigate("/book"); setIsMobileMenuOpen(false); }}
                className="block w-full text-left text-white hover:text-white/80 transition-colors py-2"
                data-testid="mobile-nav-book"
              >
                Book Session
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="hero-title">
                Learn Languages as Your Mother Tongue
              </h1>
              <p className="text-lg sm:text-xl text-white/90" data-testid="hero-description">
                Learn French, English, or Spanish through a contextual and immersive method that leads you to think, feel, and express yourself naturally. Master the language not only to communicate, but to understand the world and broaden your professional and cultural horizons.
              </p>
              <div className="flex items-center space-x-4 text-white/90">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 fill-white" />
                  <span>17+ Years Experience</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="w-5 h-5" />
                  <span>30+ Countries</span>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-white text-[#04539F] hover:bg-white/90 font-semibold px-8 py-6 text-lg rounded-full transition-all hover:scale-105"
                onClick={() => document.getElementById('programs').scrollIntoView({ behavior: 'smooth' })}
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1555436169-38f939820724" 
                alt="Language Learning" 
                className="rounded-2xl shadow-2xl"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#04539F] mb-12" data-testid="outcomes-title">
            What You'll Achieve
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Speak Naturally & Confidently", desc: "Sound authentic in real conversations" },
              { title: "Think in the Language", desc: "No more translating in your head" },
              { title: "Understand Native Speakers", desc: "Train your ear to real-life speech" },
              { title: "Use Grammar Instinctively", desc: "Speak correctly without overthinking" },
              { title: "Connect Culturally", desc: "Feel at home in the language" },
              { title: "Expand Your Horizons", desc: "Professional and cultural opportunities" }
            ].map((outcome, idx) => (
              <Card key={outcome.title} className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid={`outcome-card-${idx}`}>
                <CardContent className="p-6 space-y-3">
                  <CheckCircle2 className="w-10 h-10 text-[#04539F]" />
                  <h3 className="text-xl font-semibold text-[#04539F]">{outcome.title}</h3>
                  <p className="text-gray-600">{outcome.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Language Programs */}
      <section id="programs" className="py-20 scroll-mt-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="programs-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#04539F] mb-4" data-testid="programs-title">
            Language Programs
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            French • English • Spanish - All levels: Beginner, Intermediate, Advanced
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { lang: "French", img: "https://images.unsplash.com/photo-1673806780503-eeeb8d2139b7", key: "french" },
              { lang: "English", img: "https://images.unsplash.com/photo-1543109740-4bdb38fda756", key: "english" },
              { lang: "Spanish", img: "https://images.unsplash.com/photo-1731159101016-9608c0537bb7", key: "spanish" }
            ].map((program) => (
              <Card key={program.key} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid={`language-card-${program.key}`}>
                <img src={program.img} alt={program.lang} className="w-full h-48 object-cover" />
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#04539F] mb-4">{program.lang}</h3>
                  <p className="text-gray-600 mb-4">Immersive learning experience for all levels</p>
                  <Button 
                    className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                    onClick={() => {
                      setSelectedLanguage(program.key);
                      document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' });
                    }}
                    data-testid={`select-${program.key}-btn`}
                  >
                    Select {program.lang}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Session Details */}
          <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[#04539F] mb-4" data-testid="first-session-title">First Session Includes:</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Level diagnosis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Method description</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Goal definition</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Program adaptation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Identification of language challenges</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#04539F] mb-4" data-testid="regular-sessions-title">Regular Sessions Include:</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Comprehension activities</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Communication activities</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Pronunciation correction</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Vocabulary explanation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#04539F] mr-2 mt-1 flex-shrink-0" />
                  <span>Detailed session report (pronunciation, new vocabulary, translations)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 scroll-mt-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#04539F] mb-12" data-testid="pricing-title">Pricing Packages</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { type: "hourly", title: "1 Hour", price: "45€", hours: "1 hour", discount: null },
              { type: "10-hour", title: "10-Hour Package", price: "405€", hours: "10 hours", discount: "Save 45€", popular: true },
              { type: "20-hour", title: "20-Hour Package", price: "800€", hours: "20 hours", discount: "Save 100€" }
            ].map((pkg) => (
              <Card 
                key={pkg.type} 
                className={`relative shadow-lg hover:shadow-xl transition-all ${
                  pkg.popular ? 'border-2 border-[#04539F] scale-105' : ''
                }`}
                data-testid={`pricing-card-${pkg.type}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#04539F] text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-[#04539F]">{pkg.title}</h3>
                  <div className="text-4xl font-bold text-gray-900">{pkg.price}</div>
                  <p className="text-gray-600">{pkg.hours}</p>
                  {pkg.discount && (
                    <p className="text-green-600 font-semibold">{pkg.discount}</p>
                  )}
                  <Button 
                    className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full py-6"
                    onClick={() => {
                      setSelectedPackage(pkg.type);
                      setIsSchedulingOpen(true);
                    }}
                    data-testid={`buy-${pkg.type}-btn`}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Language Coach Program */}
      <section id="coach" className="py-20 scroll-mt-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#04539F] to-[#0668C7] text-white" data-testid="coach-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_languagecoach/artifacts/ngb2k94l_mockup%20language%20coach.png" 
                alt="Language Coach Program - WhatsApp Learning" 
                className="rounded-2xl shadow-2xl"
                data-testid="coach-image"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <MessageCircle className="w-12 h-12" />
                <h2 className="text-3xl sm:text-4xl font-bold" data-testid="coach-title">Language Coach Program</h2>
              </div>
              <p className="text-lg text-white/90">
                A personalized program to accompany learners through WhatsApp, providing continuous support with writing and speaking challenges.
              </p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 mr-3 mt-1 flex-shrink-0" />
                  <span>Daily writing challenges tailored to your level</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 mr-3 mt-1 flex-shrink-0" />
                  <span>Speaking practice with instant feedback</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 mr-3 mt-1 flex-shrink-0" />
                  <span>Personalized corrections and explanations</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 mr-3 mt-1 flex-shrink-0" />
                  <span>Progress tracking and motivation</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 mr-3 mt-1 flex-shrink-0" />
                  <span>Flexible learning at your own pace</span>
                </div>
              </div>
              <Button 
                size="lg"
                className="bg-white text-[#04539F] hover:bg-white/90 font-semibold px-8 py-6 rounded-full transition-all hover:scale-105"
                onClick={() => document.getElementById('book').scrollIntoView({ behavior: 'smooth' })}
                data-testid="coach-book-btn"
              >
                Book Your Coach Session
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 scroll-mt-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#04539F] mb-12" data-testid="testimonials-title">
            What Our Students Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Daria", country: "", text: "Bernard is a teacher whom I would recommend to everyone. He is extremely professional and pays attention to what kind of knowledge you wish to acquire but at the same time very friendly and approachable. He is creative in his teaching as he provides different exercises, suggests varied topics for discussion and gives many ideas on how to advance your language outside of class without the pressure of 'more work'. It never feels like an obligation working with him but rather something I look forward to!" },
              { name: "Mateo Lopera Rendón", country: "Colombia", text: "He estado con Bernard por 7 meses y el cambio ha sido increíble. He podido lograr hablar francés con mayor fluidez y a ganar mucha confianza en el idioma. Es un profe increíble, cercano, divertido y muy honesto. Sus clases son espacios seguros donde la experiencia educativa es amena e interactiva. He podido lograr una avance significativo que no habia logrado antes con otros profes." },
              { name: "Adriana Romero Puche", country: "Latin America", text: "Desde que comencé clases de inglés con Berni, supe que sería muy diferente an mis otras experiencias. El approach para entender el para qué necesito el idioma me ha ayudado a tener más confianza en mi. Además sus clases son muy dinámicas. 100% conversacionales y aterrizadas en mis necesidades. Tiene un dominio impecable de la gramática y corrige con mucha atención. Recomiendo muchísimo tomar clases con Berni." }
            ].map((testimonial, idx) => (
              <Card key={testimonial.name} className="shadow-lg" data-testid={`testimonial-card-${idx}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex text-[#04539F]">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-[#04539F]">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.country}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              size="lg"
              variant="outline"
              className="bg-white border-2 border-[#04539F] text-[#04539F] hover:bg-[#04539F] hover:text-white font-semibold px-8 py-6 text-lg rounded-full transition-all hover:scale-105"
              onClick={() => navigate("/reviews")}
              data-testid="view-all-reviews-btn"
            >
              View All 92 Reviews
            </Button>
          </div>
        </div>
      </section>

      {/* Acuity Scheduling */}
      <section id="book" className="py-20 scroll-mt-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="booking-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#04539F] mb-4" data-testid="booking-title">
            Book Your Session
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Schedule your first session or coaching consultation
          </p>
          <div className="bg-gray-50 rounded-2xl p-8 shadow-lg text-center" data-testid="acuity-container">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="bg-[#04539F]/10 rounded-xl p-8">
                <Calendar className="w-16 h-16 text-[#04539F] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#04539F] mb-4">Ready to Start Learning?</h3>
                <p className="text-gray-700 mb-6">
                  Click below to open our scheduling calendar and book your first session or coaching consultation. We'll work together to create a personalized learning plan that fits your goals and schedule.
                </p>
                <div className="flex justify-center">
                  <Dialog open={isSchedulingOpen} onOpenChange={setIsSchedulingOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        size="lg"
                        className="bg-[#04539F] hover:bg-[#033b7a] text-white font-semibold px-8 py-6 text-lg rounded-full transition-all hover:scale-105"
                        data-testid="open-scheduling-btn"
                      >
                        <Calendar className="w-5 h-5 mr-2" />
                        Schedule Your Session
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-4xl h-[90vh] p-0" data-testid="acuity-dialog">
                    <DialogHeader className="p-6 pb-4">
                      <DialogTitle className="text-2xl font-bold text-[#04539F]">Book Your Session</DialogTitle>
                    </DialogHeader>
                    <div className="px-6 pb-6 h-[calc(90vh-100px)] overflow-hidden">
                      <iframe 
                        src="https://app.acuityscheduling.com/schedule.php?owner=33702176" 
                        title="Schedule Appointment" 
                        width="100%" 
                        height="100%" 
                        frameBorder="0"
                        className="rounded-lg"
                        data-testid="acuity-iframe"
                      ></iframe>
                    </div>
                  </DialogContent>
                </Dialog>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-left">
                <div className="bg-white p-6 rounded-lg border-2 border-[#04539F]/20">
                  <div className="text-[#04539F] font-bold text-lg mb-2">Step 1</div>
                  <p className="text-gray-600">Select your language and package</p>
                </div>
                <div className="bg-white p-6 rounded-lg border-2 border-[#04539F]/20">
                  <div className="text-[#04539F] font-bold text-lg mb-2">Step 2</div>
                  <p className="text-gray-600">Schedule your first session</p>
                </div>
                <div className="bg-white p-6 rounded-lg border-2 border-[#04539F]/20">
                  <div className="text-[#04539F] font-bold text-lg mb-2">Step 3</div>
                  <p className="text-gray-600">Start your language journey</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#04539F] text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">LeProfBerni</h3>
          <p className="text-white/80 mb-4">17+ Years of Excellence • 30+ Countries</p>
          <p className="text-white/60 text-sm mb-4">
            © 2025 LeProfBerni. All rights reserved.
          </p>
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white/80 text-xs transition-colors"
          >
            <img
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              alt="Emergent"
              className="w-4 h-4"
            />
            Made with Emergent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
